import React, { useState } from 'react';
import SemiProdAuth from './SemiProdAuth';

interface LoginScreenProps {
  loading: boolean;
  error: string | null;
  piSDKLoaded: boolean;
  isDevelopment: boolean;
  isSemiProduction: boolean;
  isProduction: boolean;
  onPiSignIn: () => void;
  onDevBypass: () => void;
  onSemiProdAuth: (walletAddress: string) => void;
  onRetryPiSDK: () => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({
  loading,
  error,
  piSDKLoaded,
  isDevelopment,
  isSemiProduction,
  isProduction,
  onPiSignIn,
  onDevBypass,
  onSemiProdAuth,
  onRetryPiSDK,
}) => {
  const [showWalletModal, setShowWalletModal] = useState(false);

  const handleSemiProdAuth = () => {
    if (!isSemiProduction) return;
    setShowWalletModal(true);
  };

  const handleWalletSubmit = (walletAddress: string) => {
    onSemiProdAuth(walletAddress);
    setShowWalletModal(false);
  };

  return (
    <div className={`App`}>
      <header className="landing-header">
        <div className="header-content">
          <h1>🎯 Koja Classics</h1>
          <p className="tagline">Classic Board Games on Pi Network</p>
        </div>
      </header>

      <main className="landing-main">
        <section className="hero-section">
          <div className="hero-content">
            <h2>🎮 Play Classic Games, Earn Pi</h2>
            <p className="hero-description">
              Join thousands of players enjoying timeless board games like Ludo,
              Chess, and Checkers. Connect with your Pi Network account and
              start your gaming journey today!
            </p>

            <div className="features-grid">
              <div className="feature">
                <div className="feature-icon">🎲</div>
                <h3>Classic Games</h3>
                <p>Enjoy beloved board games with modern digital convenience</p>
              </div>
              <div className="feature">
                <div className="feature-icon">🏆</div>
                <h3>Compete & Win</h3>
                <p>Challenge players worldwide and climb the leaderboards</p>
              </div>
              <div className="feature">
                <div className="feature-icon">π</div>
                <h3>Pi Network</h3>
                <p>Seamlessly integrated with your Pi Network identity</p>
              </div>
            </div>

            <div className="auth-section">
              {error && (
                <div className="error-message">
                  {error}
                  {error.includes('Pi Network SDK') && (
                    <button
                      onClick={onRetryPiSDK}
                      className="retry-btn"
                      style={{
                        marginLeft: '10px',
                        padding: '5px 10px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      Retry
                    </button>
                  )}
                </div>
              )}

              {/* Development bypass button - only shows on localhost */}
              {isDevelopment && (
                <div style={{ marginBottom: '1rem' }}>
                  <button
                    onClick={onDevBypass}
                    style={{
                      background: '#ff9800',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}
                  >
                    🚀 DEV BYPASS (localhost only)
                  </button>
                  <p
                    style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.7)',
                      margin: '0.5rem 0 0 0',
                    }}
                  >
                    Skip Pi Network auth for local development
                  </p>
                </div>
              )}

              <button
                onClick={isSemiProduction ? handleSemiProdAuth : onPiSignIn}
                disabled={loading || (!piSDKLoaded && isProduction)}
                className="pi-signin-btn"
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    {isSemiProduction
                      ? 'Authenticating...'
                      : 'Loading Pi SDK...'}
                  </>
                ) : !piSDKLoaded && isProduction ? (
                  <>
                    <div className="spinner"></div>
                    Loading Pi SDK...
                  </>
                ) : (
                  <>
                    <span className="pi-icon">π</span>
                    {isSemiProduction
                      ? 'Enter Test Wallet Address'
                      : 'Sign in with Pi Network'}
                  </>
                )}
              </button>

              <p className="auth-note">
                {isSemiProduction ? (
                  <>
                    🧪 <strong>Semi-Production Mode:</strong> Enter your test
                    wallet address to continue. This is for testing purposes
                    only.
                  </>
                ) : (
                  <>
                    🔒 Secure authentication through Pi Network. Your data is
                    protected and never shared.
                  </>
                )}
              </p>
            </div>
          </div>
        </section>

        <section className="games-preview">
          <h3>🎯 Coming Soon</h3>
          <div className="games-grid">
            <div className="game-card">
              <div className="game-icon">🎲</div>
              <h3>Ludo</h3>
              <p>The most popular board game worldwide</p>
              <div className="game-players">2-4 Players</div>
              <span className="status-badge coming_soon">Coming Soon</span>
            </div>
            <div className="game-card">
              <div className="game-icon">♟️</div>
              <h3>Chess</h3>
              <p>The ultimate strategy game</p>
              <div className="game-players">2 Players</div>
              <span className="status-badge coming_soon">Coming Soon</span>
            </div>
            <div className="game-card">
              <div className="game-icon">⚫</div>
              <h3>Checkers</h3>
              <p>Classic strategy and tactics</p>
              <div className="game-players">2 Players</div>
              <span className="status-badge coming_soon">Coming Soon</span>
            </div>
          </div>
        </section>
      </main>

      {/* Semi-Production Character Selection Modal */}
      <SemiProdAuth
        showWalletModal={showWalletModal}
        setShowWalletModal={setShowWalletModal}
        loading={loading}
        error={error}
        onAuthenticate={handleWalletSubmit}
      />

      <footer className="landing-footer">
        <div className="footer-content">
          <p>&copy; 2025 Koja Classics. Built for the Pi Network community.</p>
          <div className="footer-links">
            <a href="#privacy">Privacy Policy</a>
            <a href="#terms">Terms of Service</a>
            <a href="#support">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginScreen;
